/**
 * useOperatorsList — active operators for the B.11 pickers (сдача «От
 * сотрудника», уборщики чайной). Sorted by recent change-source frequency
 * (relevance) then name. Lazily fetched: pass enabled=false to skip.
 */
import { useEffect, useState } from "react";
import { api } from "../api/client";
import type { ApiList } from "../api/types";

export interface OperatorOption {
  id: string;
  name: string;
}

interface RawOp { id: string | number; name: string }
interface RawFreq { change_source_id: string; cnt: number }

export function useOperatorsList(enabled: boolean): { operators: OperatorOption[]; loading: boolean; error: string | null } {
  const [operators, setOperators] = useState<OperatorOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || operators.length > 0) return;
    let alive = true;
    setLoading(true);
    (async () => {
      try {
        const [opsRes, freqRes] = await Promise.all([
          api<ApiList<RawOp>>("/api/game-reports/operators-list"),
          api<ApiList<RawFreq>>("/api/game-reports/change-sources").catch(() => ({ results: [] as RawFreq[] })),
        ]);
        if (!alive) return;
        const rank = new Map<string, number>();
        (freqRes.results ?? []).forEach((f, i) => rank.set(String(f.change_source_id), -(1000 - i) - Number(f.cnt || 0)));
        const list = (opsRes.results ?? [])
          .map((o) => ({ id: String(o.id), name: o.name }))
          .sort((a, b) => {
            const ra = rank.get(a.id);
            const rb = rank.get(b.id);
            if (ra != null && rb != null) return ra - rb;
            if (ra != null) return -1;
            if (rb != null) return 1;
            return a.name.localeCompare(b.name, "ru");
          });
        setOperators(list);
        setLoading(false);
      } catch (e: unknown) {
        if (!alive) return;
        setError(e instanceof Error ? e.message : "Не удалось загрузить операторов");
        setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [enabled, operators.length]);

  return { operators, loading, error };
}
